import { useEffect, useMemo, useState } from 'react';
import { useAdminApi } from '../../../lib/adminApi';
import RepeaterEditor from '../components/RepeaterEditor';

const defaults = {
  sectionId: 'Konzerte',
  title: 'KONZERTE',
  days: []
};

export default function ScheduleEditor() {
  const api = useAdminApi();
  const [doc, setDoc] = useState(defaults);
  const [orig, setOrig] = useState(defaults);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const dirty = useMemo(() => JSON.stringify(doc) !== JSON.stringify(orig), [doc, orig]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get('/sections/schedule');
        if (!alive) return;
        const normalized = normalizeLoaded(res);
        setDoc(normalized);
        setOrig(normalized);
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, []);

  const onSave = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...doc,
        days: (doc.days || []).map(syncLegacyForFallback)
      };
      await api.put('/sections/schedule', payload);
      setOrig(payload);
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spinner />;

  const readOnly = !editing;

  return (
    <div>
      <Toolbar
        title="Konzerte / Programm"
        editing={editing}
        setEditing={setEditing}
        onSave={onSave}
        onCancel={() => {
          setDoc(orig);
          setEditing(false);
        }}
        saving={saving}
        dirty={dirty}
      />
      {error && <div className="alert alert-danger m-3 py-2">{error}</div>}

      <div className="p-3">
        <Row>
          <Col>
            <Text
              label="Titel"
              value={doc.title}
              onChange={(v) => setDoc((d) => ({ ...d, title: v }))}
              readOnly={readOnly}
            />
          </Col>
        </Row>

        <RepeaterEditor
          title="Tage"
          items={doc.days}
          onChange={(v) => setDoc((d) => ({ ...d, days: v.map(syncLegacyForFallback) }))}
          template={{
            id: '',
            tabLabel: '',
            dateLabel: '',
            enabled: true,
            time: { start: '', doors: '' },
            title: '', // main day title
            items: [], // legacy fallback
            program: [
              { kind: 'work', title: '', authors: [], performers: [] } // authors[] is a list now
            ]
          }}
          renderFields={(day, patch) => (
            <>
              <div className="mb-3 d-flex align-items-center gap-3" style={{ paddingLeft: '12px' }}>
                <div className="form-check form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id={`enabled-${day.id}`}
                    checked={day.enabled !== false}
                    disabled={readOnly}
                    onChange={(e) => patch(syncLegacyForFallback({ ...day, enabled: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor={`enabled-${day.id}`}>
                    {day.enabled !== false ? 'Tag aktiv (sichtbar im Programm & Buchungsformular)' : 'Tag deaktiviert (ausgeblendet)'}
                  </label>
                </div>
              </div>
              <Row>
                <Col>
                  <Text
                    label="Tab Label"
                    value={day.tabLabel}
                    onChange={(v) => patch(syncLegacyForFallback({ ...day, tabLabel: v }))}
                    readOnly={readOnly}
                  />
                </Col>
                <Col>
                  <Text
                    label="Datum Label"
                    value={day.dateLabel}
                    onChange={(v) => patch(syncLegacyForFallback({ ...day, dateLabel: v }))}
                    readOnly={readOnly}
                  />
                </Col>
                <Col>
                  <Text
                    label="Tag-ID"
                    value={day.id}
                    onChange={(v) => patch(syncLegacyForFallback({ ...day, id: v }))}
                    readOnly={readOnly}
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Text
                    label="Beginn"
                    value={day.time?.start}
                    onChange={(v) => patch(syncLegacyForFallback({ ...day, time: { ...day.time, start: v } }))}
                    readOnly={readOnly}
                  />
                </Col>
                <Col>
                  <Text
                    label="Einlass"
                    value={day.time?.doors}
                    onChange={(v) => patch(syncLegacyForFallback({ ...day, time: { ...day.time, doors: v } }))}
                    readOnly={readOnly}
                  />
                </Col>
                <Col>
                  <Text
                    label="Tages-Titel (Main)"
                    value={day.title}
                    onChange={(v) => patch(syncLegacyForFallback({ ...day, title: v }))}
                    readOnly={readOnly}
                  />
                </Col>
              </Row>

              <ProgramEditor
                title="Programm des Tages"
                value={day.program || []}
                readOnly={readOnly}
                onChange={(program) => patch(syncLegacyForFallback({ ...day, program }))}
              />
            </>
          )}
        />
      </div>
    </div>
  );
}

/* ---------- normalize & legacy sync ---------- */

function normalizeLoaded(raw) {
  const merged = { ...defaults, ...(raw || {}) };
  merged.days = (merged.days || []).map((day, i) => {
    const d = {
      id: day.id || `day${i + 1}`,
      tabLabel: day.tabLabel || '',
      dateLabel: day.dateLabel || '',
      enabled: day.enabled !== false,
      time: day.time || { start: '', doors: '' },
      title: day.title || '',
      items: Array.isArray(day.items) ? day.items : [],
      program: Array.isArray(day.program) ? day.program : null
    };

    if (!d.program) {
      // derive one 'work' from legacy fields
      const firstLine = (d.items && d.items[0]) || '';
      d.program = [
        {
          kind: 'work',
          title: d.title || '',
          authors: firstLine ? [firstLine] : [], // convert string → single item list
          performers: (d.items || [])
            .slice(1)
            .map((line) => {
              const m = String(line).split(/[,–-]\s*/);
              return { name: m[0] || '', instrument: m.slice(1).join(' - ') || '' };
            })
        }
      ];
    } else {
      d.program = d.program.map((b) => ({
        kind: b?.kind === 'pause' ? 'pause' : 'work',
        title: b?.title || (b?.kind === 'pause' ? 'Pause' : ''),
        authors: Array.isArray(b?.authors)
          ? b.authors.filter(Boolean)
          : (b?.authors ? [String(b.authors)] : []), // if they had a string, coerce -> [string]
        performers: Array.isArray(b?.performers)
          ? b.performers.map((p) => ({ name: p?.name || '', instrument: p?.instrument || '' }))
          : []
      }));
    }

    return syncLegacyForFallback(d);
  });
  return merged;
}

function syncLegacyForFallback(day) {
  // legacy expects: items[0] = authors line (string); we join with "; "
  const firstWork = (day.program || []).find((x) => x.kind === 'work');
  const items = [];
  const authorsLine = Array.isArray(firstWork?.authors) ? firstWork.authors.filter(Boolean).join('; ') : '';
  if (authorsLine) items.push(authorsLine);
  if (firstWork?.performers?.length) {
    for (const p of firstWork.performers) {
      const line = [p.name, p.instrument].filter(Boolean).join(', ');
      if (line) items.push(line);
    }
  }
  return { ...day, enabled: day.enabled !== false, items };
}

/* ---------- Program editor ---------- */

function ProgramEditor({ title, value, onChange, readOnly }) {
  const addWork = () =>
    onChange([...(value || []), { kind: 'work', title: '', authors: [], performers: [] }]);
  const addPause = () =>
    onChange([...(value || []), { kind: 'pause', title: 'Pause', authors: [], performers: [] }]);

  return (
    <div className="mt-3">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>{title}</strong>
        {!readOnly && (
          <div className="d-flex gap-2">
            <button type="button" className="btn btn-sm btn-edit" onClick={addWork}>
              <i className="lni-plus me-1" /> Werk hinzufügen
            </button>
            <button type="button" className="btn btn-sm btn-secondary" onClick={addPause}>
              <i className="lni-coffee-cup me-1" /> Pause einfügen
            </button>
          </div>
        )}
      </div>

      {(value || []).map((b, i) => (
        <div key={i} className="border rounded p-3 mb-2">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <span className="badge-soft">{b.kind === 'pause' ? 'Pause' : `Werk #${i + 1}`}</span>
            {!readOnly && (
              <button
                type="button"
                className="btn btn-icon"
                onClick={() => {
                  const next = value.slice();
                  next.splice(i, 1);
                  onChange(next);
                }}
                title="Entfernen"
              >
                <i className="lni-close" />
              </button>
            )}
          </div>

          <div className="row">
            <div className="col-md-3">
              <label className="form-label">Typ</label>
              <select
                className="form-select"
                disabled={readOnly}
                value={b.kind === 'pause' ? 'pause' : 'work'}
                onChange={(e) => {
                  const kind = e.target.value;
                  const next = value.slice();
                  next[i] =
                    kind === 'pause'
                      ? { kind: 'pause', title: b.title || 'Pause', authors: [], performers: [] }
                      : {
                          kind: 'work',
                          title: b.title || '',
                          authors: Array.isArray(b.authors) ? b.authors : [],
                          performers: Array.isArray(b.performers) ? b.performers : []
                        };
                  onChange(next);
                }}
              >
                <option value="work">Werk</option>
                <option value="pause">Pause</option>
              </select>
            </div>

            <div className="col-md-9">
              <Text
                label={b.kind === 'pause' ? 'Pausen-Titel' : 'Programmtitel'}
                value={b.title}
                onChange={(v) => update(i, { ...b, title: v }, value, onChange)}
                readOnly={readOnly}
              />
            </div>
          </div>

          {b.kind !== 'pause' && (
            <>
              {/* Werke-Teile: dynamic list */}
              <RepeatingTextArea
                title="Werke-Teile"
                values={Array.isArray(b.authors) ? b.authors : []}
                readOnly={readOnly}
                onChange={(list) => update(i, { ...b, authors: list }, value, onChange)}
                addLabel="Teil hinzufügen"
              />

              <PerformersEditor
                value={b.performers || []}
                readOnly={readOnly}
                onChange={(performers) => update(i, { ...b, performers }, value, onChange)}
              />
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function update(idx, obj, arr, set) {
  const next = arr.slice();
  next[idx] = obj;
  set(next);
}

/* ---------- Performers with add/remove & artist autocomplete ---------- */

function PerformersEditor({ value, onChange, readOnly }) {
  const api = useAdminApi();
  const [options, setOptions] = useState([]); // {name, role}[]

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await api.get('/sections/artists');
        if (!alive || !res?.entries) return;
        setOptions(res.entries.map((e) => ({ name: e.name || '', role: e.role || '' })));
      } catch {
        /* ignore */
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line
  }, []);

  const add = () => onChange([...(value || []), { name: '', instrument: '' }]);
  const updateAt = (idx, patch) => {
    const next = (value || []).slice();
    next[idx] = { ...(next[idx] || { name: '', instrument: '' }), ...patch };
    onChange(next);
  };
  const removeAt = (idx) => onChange((value || []).filter((_, i) => i !== idx));

  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>Mitwirkende (Name &amp; Instrument)</strong>
        {!readOnly && (
          <button type="button" className="btn btn-sm btn-edit" onClick={add}>
            <i className="lni-plus me-1" /> Mitwirkende hinzufügen
          </button>
        )}
      </div>

      <datalist id="artists-name-list">
        {options.map((o, i) => (
          <option key={i} value={o.name} />
        ))}
      </datalist>

      {(value || []).map((p, i) => (
        <div key={i} className="border rounded p-2 mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Person #{i + 1}</strong>
            {!readOnly && (
              <button type="button" className="btn btn-icon" onClick={() => removeAt(i)} title="Entfernen">
                <i className="lni-close" />
              </button>
            )}
          </div>

          <div className="row">
            <div className="col-md-6">
              <label className="form-label">Name</label>
              <input
                className="form-control"
                list="artists-name-list"
                value={p?.name || ''}
                onChange={(e) => {
                  const name = e.target.value;
                  const match = options.find((o) => o.name === name);
                  if (!readOnly && match && !p.instrument) {
                    updateAt(i, { name, instrument: match.role || '' });
                  } else {
                    updateAt(i, { name });
                  }
                }}
                readOnly={readOnly}
              />
              <div className="form-text">Tippen für Vorschläge aus Künstler*innen.</div>
            </div>

            <div className="col-md-6">
              <label className="form-label">Instrument</label>
              <input
                className="form-control"
                value={p?.instrument || ''}
                onChange={(e) => updateAt(i, { instrument: e.target.value })}
                readOnly={readOnly}
              />
            </div>
          </div>
        </div>
      ))}

      {!readOnly && (value || []).length === 0 && (
        <div className="text-muted small">Noch keine Mitwirkenden. Mit „Mitwirkende hinzufügen“ starten.</div>
      )}
    </div>
  );
}

/* ---------- small helpers ---------- */

function Toolbar(p) {
  return CarouselToolbar(p);
}
function Spinner() {
  return (
    <div className="p-4 text-center">
      <div className="spinner-border brand-spinner" role="status" />
      <div className="small mt-2 text-muted">Lädt…</div>
    </div>
  );
}
const CarouselToolbar = ({ title, editing, setEditing, onSave, onCancel, saving, dirty }) => (
  <div className="editor-toolbar">
    <div className="d-flex align-items-center gap-2">
      <h4 className="editor-title m-0">{title}</h4>
      {editing && dirty && <span className="badge-soft">• Ungespeicherte Änderungen</span>}
    </div>
    <div className="d-flex align-items-center gap-2">
      {!editing ? (
        <button className="btn btn-sm btn-edit" onClick={() => setEditing(true)}>
          Bearbeiten
        </button>
      ) : (
        <>
          <button className="btn btn-sm btn-save" onClick={onSave} disabled={!dirty || saving}>
            {saving ? 'Speichert…' : 'Speichern'}
          </button>
          <button className="btn btn-sm btn-cancel" onClick={onCancel} disabled={saving}>
            Abbrechen
          </button>
        </>
      )}
    </div>
  </div>
);
function Text({ label, value, onChange, readOnly }) {
  return (
    <div className="mb-3">
      <label className="form-label">{label}</label>
      <input className="form-control" value={value || ''} onChange={(e) => onChange(e.target.value)} readOnly={readOnly} />
    </div>
  );
}
const Row = ({ children }) => <div className="row">{children}</div>;
const Col = ({ children }) => <div className="col-md-4">{children}</div>;

/* Re-usable text list */
function RepeatingTextArea({ title, values, onChange, readOnly, addLabel = 'Hinzufügen' }) {
  const add = () => onChange([...(values || []), '']);
  const update = (i, v) => onChange(values.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => onChange(values.filter((_, idx) => idx !== i));
  return (
    <div className="mt-2">
      <div className="d-flex align-items-center justify-content-between mb-2">
        <strong>{title}</strong>
        {!readOnly && (
          <button type="button" className="btn btn-sm btn-edit" onClick={add}>
            <i className="lni-plus me-1" /> {addLabel}
          </button>
        )}
      </div>
      {(values || []).map((text, i) => (
        <div key={i} className="border rounded p-2 mb-2">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <strong>Teil #{i + 1}</strong>
            {!readOnly && (
              <button type="button" className="btn btn-icon" onClick={() => remove(i)} aria-label="Entfernen" title="Entfernen">
                <i className="lni-close" />
              </button>
            )}
          </div>
          <div className="mb-2">
            <label className="form-label">Text</label>
            <textarea
              className="form-control"
              rows={2}
              value={text || ''}
              onChange={(e) => update(i, e.target.value)}
              readOnly={readOnly}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
